import React, { useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Input, Radio, message, Form } from 'antd';
import styled from 'styled-components';
import notificationApis from '../../../services/notificationApis';
import { setLoading } from '../../../store/loading/actions';
import { useDispatch } from 'react-redux';
import Button, { BUTTON_VARIANT_TYPES } from '../../../components/Button';
import QuillWrapper from './components/QuillWrapper';
import FileUpload from './components/FileUpload';
import SendListMultiSelect from './components/SendListMultiSelect';
import { ICreateOrUpdateNotificationBody } from '../../../services/apisType';
import { IResponseDataBody } from '../../../utils/response';
import { INotificationDetail } from '../services/types';

interface NotificationDetailFormValues {
  title: string;
  contentTypeRadio: 'CONTENT' | 'UPLOAD';
  shopIds: string[];
  uploadType: 'IMAGE' | 'VIDEO';
  contentHtml: string;
  contentUrl: string;
}

const initialValues: NotificationDetailFormValues = {
  title: '',
  contentTypeRadio: 'CONTENT',
  shopIds: [],
  uploadType: 'IMAGE',
  contentHtml: '',
  contentUrl: '',
};

const NotificationDetailPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm<NotificationDetailFormValues>();

  const getData = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      const response: IResponseDataBody<INotificationDetail> = await notificationApis.getNotificationDetail(id);
      if (response?.data?.data) {
        const data = response.data.data;
        form.setFieldsValue({
          title: data.title,
          shopIds: data.shopIds,
          contentTypeRadio: data.contentType === 'CONTENT' ? 'CONTENT' : 'UPLOAD',
          contentHtml: data.contentType === 'CONTENT' ? data.content : '',
          contentUrl: data.contentType === 'IMAGE' || data.contentType === 'VIDEO' ? data.content : undefined,
          uploadType: data.contentType === 'IMAGE' || data.contentType === 'VIDEO' ? data.contentType : 'IMAGE',
        });
      }
    } catch (error) {
      message.error('Failed to get notification detail');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, id, form]);

  const handleBack = () => {
    navigate('/notification');
  };

  const handleSubmit = () => {
    form.submit();
  };

  const onFinish = async (values: NotificationDetailFormValues) => {
    try {
      dispatch(setLoading(true));
      const contentType = values.contentTypeRadio === 'UPLOAD' ? values.uploadType : 'CONTENT';
      const contentByType = contentType === 'CONTENT' ? values.contentHtml : values.contentUrl;
      const body: ICreateOrUpdateNotificationBody = {
        id: id || null,
        title: values.title,
        content: contentByType,
        contentType: contentType,
        shopIds: values.shopIds,
      };
      const response = await notificationApis.createOrUpdateNotification(body);
      if (response?.data?.data) {
        message.success(id ? 'Notification updated successfully' : 'Notification created successfully');
      } else {
        message.error(response?.data?.message || 'Failed to save notification');
      }
    } catch (error: any) {
      message.error('An error occurred while saving notification');
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    if (id) {
      getData();
    }
  }, [id, getData]);

  return (
    <ContainerStyled>
      <HeaderStyled>
        <TitleStyled>Notification / {id ? 'Edit' : 'Add'} Notification</TitleStyled>
        <ButtonGroupStyled>
          <Button variant={BUTTON_VARIANT_TYPES.DEFAULT} onClick={handleBack}>
            Back
          </Button>
          <Button variant={BUTTON_VARIANT_TYPES.PRIMARY} onClick={handleSubmit}>
            OK
          </Button>
        </ButtonGroupStyled>
      </HeaderStyled>

      <FormContainerStyled>
        <Form form={form} layout='vertical' onFinish={onFinish} initialValues={initialValues}>
          <BodyStyled>
            <FormRowStyled>
              <FormFieldStyled>
                <Form.Item name='title' label='Title' rules={[{ required: true, message: 'Please enter title' }]}>
                  <Input size='large' placeholder='Please enter...' />
                </Form.Item>
              </FormFieldStyled>
              <FormFieldStyled>
                <Form.Item
                  name='shopIds'
                  label='Send List'
                  rules={[{ required: true, message: 'Please select send list' }]}
                >
                  <SendListMultiSelect />
                </Form.Item>
              </FormFieldStyled>
            </FormRowStyled>

            <FormRowStyled>
              <FormFieldStyled>
                <Form.Item name='contentTypeRadio' label='Type'>
                  <Radio.Group>
                    <RadioStyled value='CONTENT'>Content</RadioStyled>
                    <RadioStyled value='UPLOAD'>Image/Video</RadioStyled>
                  </Radio.Group>
                </Form.Item>
              </FormFieldStyled>
            </FormRowStyled>

            <FormRowStyled>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue, setFieldsValue }) => {
                  const contentTypeRadio = getFieldValue('contentTypeRadio');
                  if (contentTypeRadio === 'CONTENT') {
                    return (
                      <FormFieldFullWidthStyled>
                        <Form.Item
                          name='contentHtml'
                          label='Content'
                          rules={[{ required: true, message: 'Please enter content' }]}
                        >
                          <QuillWrapper />
                        </Form.Item>
                      </FormFieldFullWidthStyled>
                    );
                  }
                  return (
                    <FormFieldFullWidthStyled>
                      <Form.Item
                        name='contentUrl'
                        label='Image/Video'
                        rules={[{ required: true, message: 'Please upload or add link to image/video' }]}
                      >
                        <FileUpload
                          onUploadTypeChange={(type) => {
                            setFieldsValue({ uploadType: type });
                          }}
                        />
                      </Form.Item>
                    </FormFieldFullWidthStyled>
                  );
                }}
              </Form.Item>
              <Form.Item name='uploadType' noStyle>
                <Input type='hidden' />
              </Form.Item>
            </FormRowStyled>
          </BodyStyled>
        </Form>
      </FormContainerStyled>
    </ContainerStyled>
  );
};

export default NotificationDetailPage;

const ContainerStyled = styled.div`
  height: calc(100vh - 129px);
  overflow-y: auto;
  padding: 16px;
  background: #f5f5f5;
`;

const HeaderStyled = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const TitleStyled = styled.div`
  color: #1d2129;
  font-family: Poppins;
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const ButtonGroupStyled = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const FormContainerStyled = styled.div`
  background: white;
  padding: 24px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRowStyled = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FormFieldStyled = styled.div`
  flex: 1;

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-label > label {
    color: #1d2129;
    font-family: Poppins;
    font-size: 14px;
    font-weight: 500;
  }
`;

const FormFieldFullWidthStyled = styled.div`
  width: 100%;

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item-label > label {
    color: #1d2129;
    font-family: Poppins;
    font-size: 14px;
    font-weight: 500;
  }
`;

const RadioStyled = styled(Radio)`
  font-family: Poppins;
  font-size: 14px;

  .ant-radio-checked .ant-radio-inner {
    border-color: #008bff;
    background-color: #008bff;
  }

  .ant-radio:hover .ant-radio-inner {
    border-color: #008bff;
  }
`;

const BodyStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
